import type {SurveyAnswer, SurveyFieldValue, SurveyFormValues, SurveyOption, SurveyResponse, SurveySpec, SurveyStep} from './survey.types';
import {ASPECT_RATIO_KEEP_PINNED_OPTION, ASPECT_RATIO_SURVEY_STEP_KEY, ASSET_OPTION_NAME_SEPARATOR, SurveyFieldKind} from './survey.types';
import type {AnsweredFormSteps, SurveyPicks} from '@Components/ai-chat-window/ai-chat-window.types';

const OTHER_SUFFIX = '__other';

/** The form-field name that holds a step's free-text "other" entry. */
export function otherKey(key: string): string {
  return `${key}${OTHER_SUFFIX}`;
}

export function emptyValueForFieldKind(kind: SurveyFieldKind): SurveyFieldValue {
  if (kind === SurveyFieldKind.MULTISELECT || kind === SurveyFieldKind.ASSET_GRID) {
    return [];
  }

  return '';
}

export function presentText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  if (trimmed === '') {
    return undefined;
  }

  return trimmed;
}

/** Returns the value when the field counts as answered (non-empty), otherwise undefined. */
export function presentAnswer(value: unknown): SurveyFieldValue | undefined {
  const text = presentText(value);

  if (text !== undefined) {
    return text;
  }

  if (Array.isArray(value) && value.length > 0) {
    return value as string[];
  }

  return undefined;
}

/**
 * Shape the raw react-hook-form values into the SurveyResponse contract: for each step,
 * keep the present answer and/or the trimmed free-text "other" entry, dropping steps that
 * have neither. Pure — the component passes in the current values and the spec's steps.
 */
export function buildResponse(values: SurveyFormValues, steps: SurveyStep[]): SurveyResponse {
  const out: SurveyResponse = {};

  steps.forEach((step) => {
    const answer = presentAnswer(values[step.key]);
    const other = presentText(values[otherKey(step.key)]);
    const entry: SurveyAnswer = {};

    if (answer !== undefined) {
      entry.value = answer;
    }
    if (other !== undefined) {
      entry.other = other;
    }
    if (entry.value !== undefined || entry.other !== undefined) {
      out[step.key] = entry;
    }
  });

  return out;
}

export function assetOptionName(option: SurveyOption): string {
  return option.description === undefined || option.description === '' ? option.label : `${option.label}${ASSET_OPTION_NAME_SEPARATOR}${option.description}`;
}

const optionLabelForValue = (step: SurveyStep, value: string): string => {
  if (step.field.kind === SurveyFieldKind.ASSET_GRID) {
    const option = step.field.options.find((assetOption) => assetOption.value === value);
    return option === undefined ? value : assetOptionName(option);
  }

  if (step.field.kind === SurveyFieldKind.SELECT || step.field.kind === SurveyFieldKind.MULTISELECT) {
    return step.field.options.find((option) => option.value === value)?.label ?? value;
  }

  return value;
};

const answerValuesOf = (answer: SurveyAnswer): string[] => {
  if (answer.value === undefined) {
    return [];
  }

  return Array.isArray(answer.value) ? answer.value : [answer.value];
};

const formatSurveyAnswer = (step: SurveyStep, answer: SurveyAnswer): string => {
  const parts: string[] = [];
  const labels = answerValuesOf(answer).map((value) => optionLabelForValue(step, value));

  if (labels.length > 0) {
    parts.push(labels.join(', '));
  }

  if (answer.other !== undefined && answer.other !== '') {
    parts.push(answer.other);
  }

  return parts.filter((part) => part !== '').join(', ');
};

/**
 * Whether a size-conflict answer gives up the pinned ratio. Null means a dismissed form.
 * A typed "something else" answer does not: the design step judges those words and reports
 * back through the pin-dropped control event.
 */
export function answerGivesUpPinnedAspectRatio(response: SurveyResponse | null): boolean {
  if (response === null) {
    return true;
  }

  const answer = response[ASPECT_RATIO_SURVEY_STEP_KEY];
  const pickedOption = answer?.value;

  if (typeof pickedOption === 'string' && pickedOption !== '') {
    return pickedOption !== ASPECT_RATIO_KEEP_PINNED_OPTION;
  }

  return presentText(answer?.other) === undefined;
}

const optionsOf = (step: SurveyStep): SurveyOption[] => (step.field.kind === SurveyFieldKind.TEXT ? [] : step.field.options);

const pickedValuesOf = (step: SurveyStep, answer: SurveyAnswer): string[] => {
  const offeredValues = optionsOf(step).map((option) => option.value);

  return answerValuesOf(answer).filter((value) => offeredValues.includes(value));
};

/**
 * The options the user picked, named by value for the server to check against the form it showed. Typed answers are not
 * picks and stay in the message text; undefined when nothing was picked.
 */
export function buildSurveyPicks(spec: SurveySpec, response: SurveyResponse): SurveyPicks | undefined {
  const picks: SurveyPicks['picks'] = {};

  spec.steps.forEach((step) => {
    const answer = response[step.key];
    const pickedValues = answer === undefined ? [] : pickedValuesOf(step, answer);

    if (pickedValues.length > 0) {
      picks[step.key] = pickedValues;
    }
  });

  return Object.keys(picks).length === 0 ? undefined : {surveyId: spec.surveyId, picks};
}

/**
 * The steps the user answered, typed or picked, named by key for the server to check against the form it showed: the
 * server tells the model which questions went unanswered, so the user's own reply never has to say so.
 */
export function buildAnsweredFormSteps(spec: SurveySpec, response: SurveyResponse): AnsweredFormSteps {
  return {surveyId: spec.surveyId, stepKeys: spec.steps.filter((step) => response[step.key] !== undefined).map((step) => step.key)};
}

export function renderSurveyResponseEcho(spec: SurveySpec, response: SurveyResponse): string {
  const blocks: string[] = [];

  for (const step of spec.steps) {
    const answer = response[step.key];
    if (!answer) {
      continue;
    }

    blocks.push(`**Q:** ${step.helpText}  \n**A:** ${formatSurveyAnswer(step, answer)}`);
  }

  if (blocks.length === 0) {
    return `> *${window.i18next.t('pmwjs_ai_chat_survey_skipped')}*`;
  }

  return blocks.join('\n\n');
}
